
import {
  defineComponent,
  onBeforeMount,
  Ref,
  ref,
  watch,
  computed,
  onBeforeUnmount
} from "vue";

export default defineComponent({
  name: "pagination",
  props: {
    qtPages: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    height: {
      type: String,
      default: "60px"
    },
    hasBackground: {
      type: Boolean,
      default: true
    },
    roundedStyle: {
      type: String,
      default: "full"
    },
    itemSize: {
      type: String,
      default: "large"
    },
    primaryColor: {
      type: String,
      default: "#31639f"
    },
    justifyContent: {
      type: String,
      default: "center"
    }
  },

  setup(props, { emit }) {
    /** Controls if the "next" button is disabled */
    const isNextDisabled: Ref<boolean> = ref(false);

    /** Controls if the "previous" button is disabled */
    const isPreviousDisabled: Ref<boolean> = ref(false);

    /** Pages array that is shown on the screen (including the "...") */
    const pages: Ref<Array<number>> = ref([]);

    /** Complete array with all the pages */
    let totalPagesArray: number[] = [];

    /** Pagination height, controlled by the prop */
    const pagHeight = ref(props.height);

    /** On component mount, setup the pages and button states */
    onBeforeMount(async () => {
      await generatePageArray();
      await handleButtonStyles();
      await setPagesLinkVisibility();
    });

    /** Watch for currentPage changes and update button states and page visibility */
    watch(
      () => props.currentPage,
      async () => {
        await handleButtonStyles();
        await setPagesLinkVisibility();
      }
    );

    /** Watch for qtPages changes (total pages) and regenerate the array */
    watch(
      () => props.qtPages,
      async () => {
        await handleButtonStyles();
        await generatePageArray();
        await setPagesLinkVisibility();
      }
    );

    /** Display the page number or "..." based on NaN */
    const setPageLink = (page): string => page || "...";

    /** Check if the given page is the current page */
    const isCurrentPage = (page): boolean => page === props.currentPage;

    /** Check if the page is a number (not the ellipsis) */
    const isNotEllipsis = (page): boolean => !!page;

    /** Generate the array of pages based on the total pages */
    async function generatePageArray(): Promise<void> {
      totalPagesArray = Array.from({ length: props.qtPages }, (_, i) => i + 1);
    }

    /** Enable/disable next/previous buttons based on the current page */
    async function handleButtonStyles(): Promise<void> {
      isNextDisabled.value = props.currentPage >= props.qtPages;
      isPreviousDisabled.value = props.currentPage <= 1 || props.qtPages <= 1;
    }

    /** Handle the logic of what pages should be visible and when to show "..." */
    async function setPagesLinkVisibility(): Promise<void> {
      if (isCompact.value) {
        if (props.qtPages <= 5) {
          pages.value = totalPagesArray;
          return;
        }

        if (props.currentPage <= 2) {
          pages.value = [1, 2, 3,4, props.qtPages];
        } else if (props.currentPage >= props.qtPages - 3) {
          pages.value = [
            1,
            props.qtPages - 3,
            props.qtPages - 2,
            props.qtPages - 1,
            props.qtPages
          ];
        } else {
          pages.value = [
            1,
            props.currentPage - 1,
            props.currentPage,
            props.currentPage + 1,
            props.qtPages
          ];
        }
        return;
      }

      if (props.qtPages <= 7) {
        pages.value = totalPagesArray;
      } else if (props.currentPage <= 4) {
        pages.value = [...totalPagesArray.slice(0, 5), NaN, props.qtPages];
      } else if (props.currentPage > 4 && props.currentPage <= props.qtPages - 4) {
        pages.value = [
          1,
          NaN,
          ...totalPagesArray.slice(props.currentPage - 2, props.currentPage + 1),
          NaN,
          props.qtPages
        ];
      } else {
        pages.value = [1, NaN, ...totalPagesArray.slice(props.qtPages - 5)];
      }
    }


    /** Go to the next page if not already at the last page */
    const nextPage = () =>
      props.currentPage < props.qtPages &&
      emit("change-page", props.currentPage + 1);

    /** Go back to the previous page if not already at the first page */
    const previousPage = () =>
      props.currentPage > 1 && emit("change-page", props.currentPage - 1);

    /** Jump to a specific page */
    const skip = (page: number) => page && emit("change-page", page);

    /** Computed class for rounded style */
    const roundedClass = computed(() =>
      props.roundedStyle === "full" ? "rounded-full" : "rounded-default"
    );

    /** Computed class for button rounded style */
    const buttonRoundedClass = computed(() =>
      props.roundedStyle === "full" ? "btn-rounded-full" : "btn-rounded-default"
    );

    /** Computed dynamic style for page items */
    const itemStyle = computed(() => ({
      width: props.itemSize === "large" ? "40px" : "30px",
      height: props.itemSize === "large" ? "40px" : "30px",
      borderRadius: props.roundedStyle === "full" ? "50%" : "10px"
    }));

    const isCompact = ref(window.innerWidth < 400);

    const handleResize = () => {
      isCompact.value = window.innerWidth < 400;
      setPagesLinkVisibility();
    };

    onBeforeMount(() => {
      window.addEventListener("resize", handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
    });

    return {
      pages,
      isPreviousDisabled,
      isNextDisabled,
      pagHeight,
      setPageLink,
      nextPage,
      previousPage,
      skip,
      isCurrentPage,
      isNotEllipsis,
      roundedClass,
      buttonRoundedClass,
      itemStyle
    };
  },
});
